import { useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";
import uniq from "lodash/uniq";
import { useSelector, useDispatch } from "react-redux";

import type { ExportTabs } from "app/pages/sales-intelligence/common-components/export-table/constants";
import { MANAGE_INTEGRATION_ROUTE } from "app/pages/sales-intelligence/constants/sl_routes";
import { industryQueryKeys } from "app/pages/sales-intelligence/modules/industry/queries/queryKeyFactory";
import {
    setRecordToEnableExport,
    unloadInvalidPredefined,
} from "app/pages/sales-intelligence/sub-modules/management/store/actions/common";
import {
    setTabNameThunk,
    trackEnableRecordExportCanceled,
} from "app/pages/sales-intelligence/sub-modules/management/store/effects/common";
import type { putSfAccountsToggleThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/toggles";
import {
    selectCurrentExportTab,
    selectRecordToEnableExport,
    selectAllInvalidPreDefinedFields,
    selectTabsWithValidationError,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/common";
import { ExportNotification } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { ExportName } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { exportNameToTabIndex, tabIndexToExportName } from "../utils/utils";

import { useIsCompanyAsLeadClaimEnabled } from "./useIsCompanyAsLeadClaimEnabled";
import { useManageExportProps } from "./useManageExportProps";

export const useManageExport = (integrationName: Integration) => {
    const isCompanyAsLeadExportClaimEnabled = useIsCompanyAsLeadClaimEnabled(integrationName);
    const tabName = useSelector(selectCurrentExportTab);
    const recordToEnableExport = useSelector(selectRecordToEnableExport);

    const invalidPreFields = useSelector(selectAllInvalidPreDefinedFields);

    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const {
        isSaveLoadingState,
        setNotificationAction,
        isLoadingState,
        saveAllExportSettings,
        selectSettingsCount,
        selectMissingRequiredFieldsCount,
        putIsRecordExportEnabled,
        changedTabNames,
        isEmptyFieldsExist,
        tabsWithEmptyFields,
        selectSettings,
    } = useManageExportProps(integrationName);

    const missingRequiredFieldsCount = useSelector(selectMissingRequiredFieldsCount);

    const tabNamesWithChanges = useSelector(changedTabNames);
    const isChanged = !!tabNamesWithChanges.length;
    const isSettingsLoading = useSelector(isLoadingState);
    const isAnyFieldEmpty = useSelector(isEmptyFieldsExist);
    const isSaveSettingsLoading = useSelector(isSaveLoadingState);
    const settings = useSelector(selectSettings);

    const emptyFieldsTabs = useSelector(tabsWithEmptyFields);
    const validationErrorTabs = useSelector(selectTabsWithValidationError);

    const tabsWithErrors = uniq([...emptyFieldsTabs, ...validationErrorTabs]);

    const isDisabledSave = useMemo(() => {
        if (invalidPreFields.length) {
            return true;
        }

        return !isChanged || isAnyFieldEmpty;
    }, [isChanged, isAnyFieldEmpty, invalidPreFields]);

    const settingsCount = useSelector(selectSettingsCount);
    const isFooterVisible = !!settingsCount;

    const saveTooltipContent = useMemo(() => {
        if (invalidPreFields.length || isAnyFieldEmpty || isChanged) {
            return "si.management.sf.settings_export.settings.btn.save.disabled.tooltip.has_unmapped_fields";
        }

        return "si.management.integration.export.settings.btn.save.tooltip.no.changes";
    }, [isAnyFieldEmpty, invalidPreFields, isChanged]);

    const onClickSave = async () => {
        const { successfullySavedSettings } = await dispatch(saveAllExportSettings(tabNamesWithChanges));

        if (successfullySavedSettings.length > 0) {
            void queryClient.invalidateQueries({ queryKey: industryQueryKeys.crmJobsTransition() });
        }

        const shouldEnableRecordExport =
            recordToEnableExport &&
            successfullySavedSettings.includes(recordToEnableExport) &&
            missingRequiredFieldsCount[recordToEnableExport] === 0;

        if (!shouldEnableRecordExport) {
            return;
        }

        if (putIsRecordExportEnabled.hasOwnProperty(recordToEnableExport)) {
            const setIsExportRecordEnabled = (
                putIsRecordExportEnabled as Record<ExportName, typeof putSfAccountsToggleThunk>
            )[recordToEnableExport];
            dispatch(setIsExportRecordEnabled(true));
        }

        const integrationText = `${integrationName} integration`;

        allTrackers.dispatchTrackEvent(
            integrationText,
            "export activated",
            `activated successfully/${recordToEnableExport}/${integrationText}`,
        );

        dispatch(setRecordToEnableExport(null));
    };

    const setNotification = (notification: ExportNotification | null) => dispatch(setNotificationAction(notification));

    const onClickCancel = () => {
        if (isChanged) {
            setNotification(ExportNotification.UNSAVED_FIELDS);
        } else {
            proNavigator.go(MANAGE_INTEGRATION_ROUTE[integrationName]);
        }
    };

    const setTabIndex = (tabIndex: ExportTabs) => {
        dispatch(setTabNameThunk(tabIndexToExportName(tabIndex, isCompanyAsLeadExportClaimEnabled)));
    };

    const cleanup = () => {
        dispatch(unloadInvalidPredefined());
        dispatch(trackEnableRecordExportCanceled(integrationName));
        dispatch(setTabNameThunk("accounts"));
    };

    return {
        onClickSave,
        onClickCancel,
        setNotification,
        setTabIndex,
        cleanup,
        tabName,
        tabIndex: exportNameToTabIndex(tabName),
        isSettingsLoading: isSettingsLoading || (tabName !== "companyAsLeads" && settings.length === 0),
        isSaveSettingsLoading,
        tabsWithErrors,
        isDisabledCancel: isSaveSettingsLoading,
        isDisabledSave,
        isFooterVisible,
        saveTooltipContent,
        isSettingsChanged: isChanged,
        missingRequiredFieldsCount,
    };
};
